<template>
  <validation-provider
    :rules="rules"
    ref="fileInput"
    v-slot="{ errors }"
    :skip-if-empty="true"
    :name="name"
  >
    <form class="user_photo" style="position: relative" ref="formPicture">
      <v-overlay absolute :value="loading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-img contain :src="image" :height="height"></v-img>
      <div class="upload">
        <v-btn
          :loading="loading"
          :disabled="loading"
          text
          dense
          :data-test-upload="name"
          @click="$refs.nativeFile.click()"
          :color="options.bgColor"
          :class="{ shadow: options.shadow }"
          small
          fab
        >
          <v-icon :color="options.textColor">
            mdi-cloud-upload
          </v-icon>
          <input
            ref="nativeFile"
            @change="filePick($event)"
            style="display: none;"
            type="file"
          />
        </v-btn>
        <v-btn
          text
          dense
          :data-test-copy="name"
          @click="copyToClipboard(id)"
          v-if="id && !isLocalSelected"
          class="ml-3"
          :color="options.bgColor"
          :class="{ shadow: options.shadow }"
          small
          fab
        >
          <v-icon :color="options.textColor">
            mdi-content-copy
          </v-icon>
        </v-btn>

        <v-btn
          v-if="(value || id) && canDelete && isNothingChoosed"
          text
          :data-test-delete="name"
          dense
          :color="options.bgColor"
          :class="{ shadow: options.shadow }"
          class="ml-3"
          small
          fab
          @click="clearFile"
        >
          <v-icon :color="options.textColor">
            mdi-delete
          </v-icon>
        </v-btn>
      </div>
    </form>
    <div class="mt-2 subtitle-1" v-if="mediaObject.name && !isLocalSelected">
      Name: {{ mediaObject.name }}
    </div>
    <div class="subtitle-1" v-if="mediaObject.size && !isLocalSelected">
      Size: {{ formatBytes(mediaObject.size) }}
    </div>

    <div transition="scroll-y-transition">
      <div
        class="v-messages theme--light error--text mt-2"
        role="alert"
        v-if="errors.length > 0"
      >
        <div class="v-messages__wrapper">
          <div class="v-messages__message">
            {{ errors[0] }}
          </div>
        </div>
      </div>
    </div>
  </validation-provider>
</template>

<script>
import { computed, reactive, ref, toRefs, watch } from '@vue/composition-api';

import { useLazyQuery, useMutation, useResult } from '@vue/apollo-composable';

import useMedia from '@/compositions/useMedia';

import { ValidationProvider, extend } from 'vee-validate';

import { required } from 'vee-validate/dist/rules.js';
import convertBytes from '@/utils/convertBytes';

import mediaSchema from '@/api/medias/item.graphql';
import formatBytes from '@/utils/convertBytes';
import copyToClipboard from '@/utils/copyToClipboard';
import gql from '@/api/objects/item.graphql';

extend('required', required);

extend('size', (value, [size]) => {
  if (!value || value === true) return true;

  const getSize = () => value.size / 1024 / 1024;
  if (getSize() <= +size) {
    return true;
  }

  return `File should be less than ${convertBytes(+size * 1024 * 1024)}`;
});

extend('image', files => {
  if (!files || !files.length || files === true) {
    return true;
  } else {
    const isValid = () => {
      const regex = /\.(jpg|svg|jpeg|png|bmp|gif|webp)$/i;
      if (Array.isArray(files)) {
        return files.every(function(file) {
          return regex.test(file.name);
        });
      }
      return regex.test(files.name);
    };

    if (isValid()) {
      return true;
    } else {
      return 'File must be an image';
    }
  }
});

const STUB_MEDIA = '/img/empty-media.svg';
const STUB_DOCUMENT = '/img/non-image-media.svg';
const DEFAULT_HEIGHT = 120;
const HEIGHT_WHEN_SELECTED = 240;

export default {
  name: 'MediaInput',
  props: {
    rules: {
      type: String,
      default: 'image|size:20'
    },
    name: {
      type: String,
      default: 'File'
    },
    value: {
      type: [File, Number],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: null
    },
    mediaType: {
      type: String,
      default: 'image'
    }
  },
  components: {
    ValidationProvider
  },
  setup(props, { emit }) {
    const { mutate: deleteObject } = useMutation(gql.remove);

    const options = {
      bgColor: 'transparent',
      textColor: 'white',
      showTitle: false,
      shadow: true
    };

    const fileInput = ref(null);
    const nativeFile = ref(null);

    const lazyQuery = useLazyQuery(
      mediaSchema.loadMedia,
      {
        id: ''
      },
      {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only'
      }
    );

    const mediaObject = useResult(lazyQuery.result, [], data => {
      return data.object;
    });

    const state = reactive({
      image: STUB_MEDIA,
      error: [],
      height: DEFAULT_HEIGHT,
      canDelete: true,
      isLocalSelected: false
    });

    const { getImageById, isImage } = useMedia();

    const clearFile = () => {
      state.isLocalSelected = true;
      if (props.id) {
        state.image = STUB_MEDIA;
        state.height = DEFAULT_HEIGHT;
        emit('input', null);
      } else {
        nativeFile.value.value = '';
        state.error = [];
        state.image = isImage(props.mediaType) ? STUB_MEDIA : STUB_DOCUMENT;
        state.height = DEFAULT_HEIGHT;
        state.canDelete = false;
        setImagePreview(props.id);
        emit('input', null);
      }
    };

    const isNothingChoosed = computed(() => state.height !== DEFAULT_HEIGHT);

    const filePick = async e => {
      fileInput.value.errors = [];
      fileInput.value.validate(e.target.files[0]).then(({ valid }) => {
        if (!valid) return;

        const [file] = e.target.files;

        if (file?.type || props?.mediaType) {
          if (isImage(file.type)) {
            state.image = URL.createObjectURL(file);
          } else {
            state.image = isImage(props.mediaType) ? STUB_MEDIA : STUB_DOCUMENT;
          }
        } else {
          state.image = STUB_MEDIA;
        }

        state.canDelete = true;
        state.height = HEIGHT_WHEN_SELECTED;

        state.isLocalSelected = true;
        emit('input', file);
      });
    };

    const setImagePreview = mediaId => {
      if (mediaId) {
        state.height = HEIGHT_WHEN_SELECTED;
        state.image = isImage(props.mediaType)
          ? getImageById(mediaId)
          : STUB_DOCUMENT;
      }
    };

    watch(
      () => props.id,
      () => {
        if (props.id) {
          state.canDelete = true;
          setImagePreview(props.id);
          lazyQuery.variables.value.id = props.id;
          lazyQuery.load();
        }
      },
      {
        immediate: true
      }
    );

    return {
      ...toRefs(state),
      clearFile,
      filePick,
      options,
      fileInput,
      nativeFile,
      mediaObject,
      formatBytes,
      copyToClipboard,
      isNothingChoosed
    };
  }
};
</script>

<style lang="scss" scoped>
.user_photo {
  position: relative;
  background-color: #dbdbdb;
  background-position: center;
  min-height: 120px;
}

.upload {
  position: absolute;
  top: 15px;
  right: 15px;
}

.shadow {
  background-color: rgba(0, 0, 0, 0.44) !important;
  border-color: rgba(0, 0, 0, 0.44) !important;
}
</style>
